/**
 * PWA install-session bridge — consumer side.
 *
 * Runs ONCE on bootstrap, BEFORE auth / session validation. If the URL has
 * a `?install_token=<token>` query param (added by `installPrompt.view.js`
 * on the Safari side just before the user installed the PWA to their home
 * screen), this module:
 *
 *   1. Calls the `claimInstallSession` Cloud Function with the token + the
 *      current PWA's device id.
 *   2. The function writes `trustedDevices/{phone}/{deviceId}` server-side,
 *      so the subsequent `registerOrLogin` will skip OTP.
 *   3. Receives the user's `phone`, `plate`, `year` from the token and
 *      writes them to `LocalState` so the login form auto-submits.
 *   4. Removes the `install_token` query param from the URL so a refresh
 *      doesn't re-try (the token is single-use anyway).
 *
 * Failures are non-fatal — the user just sees the normal login form.
 */

import { LocalState } from '../../core/localState.js';
import { getFirebase, ensureAuthReady } from '../../core/firebase.js';
import { Firebase } from '../../app/config.js';
import { getDeviceId } from '../auth/device.service.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('pwa-bridge');

const QUERY_PARAM = 'install_token';
const LS_KEY = 'c2c_install_token';

/**
 * Look for an `install_token` in either the URL query string OR localStorage
 * (Safari may strip the URL during "Add to Home Screen" depending on iOS
 * version + manifest start_url; localStorage IS shared between Safari and
 * the PWA on iOS 16.4+ for the same origin). Exchange whichever channel
 * provided the token via the Cloud Function and seed LocalState with the
 * resulting credentials.
 *
 * @returns {Promise<{ claimed: boolean; phone?: string; plate?: string; year?: string }>}
 */
export async function claim() {
  try {
    const url = new URL(window.location.href);
    let token = url.searchParams.get(QUERY_PARAM);
    let source = 'url';
    if (!token) {
      try { token = localStorage.getItem(LS_KEY); }
      catch { /* ignore */ }
      source = 'localStorage';
    }
    if (!token) return { claimed: false };

    log.info('claim: found token via', source);

    // Remove the token from both channels right away — single-use, no
    // accidental refreshes, no leaks via shared URLs.
    url.searchParams.delete(QUERY_PARAM);
    const cleanUrl = url.pathname + (url.search ? url.search : '') + url.hash;
    window.history.replaceState(null, '', cleanUrl);
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }

    // The Cloud Function requires the caller to be authenticated (any
    // anonymous Firebase user qualifies).
    await ensureAuthReady();
    const functions = getFirebase().app().functions(Firebase.functionsRegion);
    const result = await functions.httpsCallable('claimInstallSession')({
      token,
      deviceId: getDeviceId(),
    });
    const data = result && result.data;
    if (!data || !data.phone || !data.plate) {
      log.warn('claim returned empty payload');
      return { claimed: false };
    }

    // Seed LocalState so the login form auto-submits with these values.
    LocalState.phone = data.phone;
    LocalState.plate = data.plate;
    LocalState.carYear = data.year;
    LocalState.termsAccepted = true;
    log.info('install-session claimed → phone=', data.phone);

    return {
      claimed: true,
      phone: data.phone,
      plate: data.plate,
      year: data.year,
    };
  } catch (err) {
    log.warn('claim failed:', err && err.message);
    return { claimed: false };
  }
}
